import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { policyAPI, showApiError } from '../../services/api';
import './PolicyPage.css';

const POLICY_TYPE_ALIASES = {
  terms: 'TERMS_OF_SERVICE',
  privacy: 'PRIVACY_POLICY',
};

const resolvePolicyType = (slug) => {
  if (!slug) return null;
  const normalized = slug.trim().toUpperCase();
  if (normalized === 'TERMS_OF_SERVICE' || normalized === 'PRIVACY_POLICY') {
    return normalized;
  }
  return POLICY_TYPE_ALIASES[slug.trim().toLowerCase()] || null;
};

const formatEffectiveDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('ko-KR');
};

const isHtmlContent = (content) => /<[^>]+>/.test(content || '');

const PolicyPage = () => {
  const navigate = useNavigate();
  const { policySlug } = useParams();
  const policyType = useMemo(() => resolvePolicyType(policySlug), [policySlug]);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!policyType) {
      setLoading(false);
      setError('요청하신 정책을 찾을 수 없습니다.');
      setPolicy(null);
      return;
    }

    let cancelled = false;

    const loadPolicy = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await policyAPI.getActivePolicy(policyType);
        if (!cancelled) {
          setPolicy(data);
        }
      } catch (err) {
        if (!cancelled) {
          showApiError(err, '정책을 불러오지 못했습니다.');
          setPolicy(null);
          setError('정책을 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPolicy();

    return () => {
      cancelled = true;
    };
  }, [policyType]);

  if (!policyType) {
    return (
      <div className="policy-page">
        <div className="policy-state">요청하신 정책을 찾을 수 없습니다.</div>
      </div>
    );
  }

  return (
    <div className="policy-page">
      <div className="policy-page-header">
        <button type="button" className="policy-back-link" onClick={() => navigate(-1)}>
          ← 이전으로
        </button>
        <h1>{policy?.title || '정책'}</h1>
        {policy?.version && (
          <p className="policy-meta">
            버전 {policy.version}
            {formatEffectiveDate(policy.effectiveDate) && (
              <> · 시행일 {formatEffectiveDate(policy.effectiveDate)}</>
            )}
          </p>
        )}
      </div>

      {loading ? (
        <div className="policy-state">정책을 불러오는 중…</div>
      ) : error ? (
        <div className="policy-state">{error}</div>
      ) : isHtmlContent(policy?.content) ? (
        <article
          className="policy-content policy-html-content"
          dangerouslySetInnerHTML={{ __html: policy?.content || '' }}
        />
      ) : (
        <article className="policy-content policy-plain-content">{policy?.content}</article>
      )}
    </div>
  );
};

export default PolicyPage;
